import {
  FeatureDefinition,
  GrowthBookProvider as Provider,
} from '@monofront/business/contexts';
import useI18n from '@hooks/i18n/useI18n';
import { growthBookService } from '@services/businessContainer';
import logger from '@utils/logger';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import useDeviceId from '@hooks/useDeviceId';
import { useMediaQuery } from '@monofront/business/hooks';
import { useCacheFirstAuthentication } from './persistedAuthContext';
import { useBeforeMount } from '@monofront/business/hooks';
import { useCookies } from 'react-cookie';
import { ICustomer } from '@monofront/types';

const {
  publicRuntimeConfig: { growthBookEndpoint },
} = getConfig();

interface IGrowthBookProviderProps {
  children: React.ReactNode;
  featureFlags: Record<string, FeatureDefinition<any>>;
}

interface IGrowthBookAttributes {
  loggedIn: boolean;
  tags: string[];
  locale: string;
  channel: string;
  id: string | undefined;
  deviceId: string;
  deviceType: 'mobile' | 'desktop';
  postalCode: string | undefined;
  country: string | undefined;
  totalOrderPaid: number;
  path: string;
  url: string;
  clientId: string | undefined;
  mobileAppVersion: string | undefined;
}

/**
 * Context that retrieves feature flags and configures GrowthBook
 * @returns GrowthBookProvider
 */
const GrowthBookProvider: React.FC<IGrowthBookProviderProps> = ({
  children,
  featureFlags,
}) => {
  const router = useRouter();
  const { locale, channel } = useI18n();
  const deviceId = useDeviceId();
  const [cookies] = useCookies(['_ga']);
  const isMobile = useMediaQuery(800);

  useEffect(() => {
    const mobileAppVersion = navigator.userAgent.match(/lf-app\/([^\s]+)/)?.[1];

    const attributes: Partial<IGrowthBookAttributes> = {
      locale,
      channel,
      deviceId,
      deviceType: isMobile ? 'mobile' : 'desktop',
      path: router.asPath.split('?')[0],
      url: router.asPath,
      clientId: cookies._ga ?? undefined, // google clientId
      mobileAppVersion,
    };

    growthBookService.setGrowthbookAttributes(attributes);
  }, [isMobile, router.asPath, deviceId]);

  useEffect(() => {
    growthBookService
      .getFeatureFlags(growthBookEndpoint)
      .then((featureFlags) => {
        if (!featureFlags) {
          logger.error('Error occurred while getting the features flags');

          return;
        }

        growthBookService.setPayload(featureFlags);
      })
      .catch((e: any) => {
        logger.error('Error occurred while getting the features flags', {
          error: e,
        });
      });
  }, [router.pathname]);

  useBeforeMount(() => {
    growthBookService.setPayload(featureFlags);
    growthBookService.setGrowthbookAttributes({
      locale,
      channel,
      deviceId,
      deviceType: isMobile ? 'mobile' : 'desktop',
      url: router.asPath,
    });
  });

  return (
    <Provider growthbook={growthBookService.growthbook}>{children}</Provider>
  );
};

const SyncGrowthBookAttributesProvider = () => {
  const { customer, isAuthenticated } = useCacheFirstAuthentication();

  useBeforeMount(() => {
    growthBookService.setGrowthbookAttributes({
      loggedIn: isAuthenticated,
      tags: customer?.omsTags ?? [],
      id: customer?.uid,
      postalCode: customer?.address?.postalCode,
      country: customer?.address?.country,
      totalOrderPaid: customer?.totalOrderPaid || 0,
      orderCount: (customer as ICustomer)?.orderCount || 0,
    });
  });

  useEffect(() => {
    const attributes: Partial<IGrowthBookAttributes> = {
      loggedIn: isAuthenticated,
      tags: customer?.omsTags ?? [],
      id: customer?.uid,
      postalCode: customer?.address?.postalCode,
      country: customer?.address?.country,
      totalOrderPaid: customer?.totalOrderPaid || 0,
    };

    growthBookService.setGrowthbookAttributes(attributes);
  }, [isAuthenticated, customer?.address, customer?.omsTags, customer?.uid]);

  return null;
};

export { GrowthBookProvider, SyncGrowthBookAttributesProvider };
